import jwtDecode, {JwtPayload} from "jwt-decode";
import React from "react";
import {Jwt} from "../../data/auth/Jwt";
import {UserSession} from "../../data/auth/UserSession";
import {delayed} from "../../libraries/util/Delayed";
import {GlobalState} from "../../global/GlobalState";
import {Configuration} from "../../global/Configuration";
import {KiziUser} from "../../data/auth/KiziUser";
import {Instant} from "@js-joda/core";
import {GlobalEvents, UserLoggedOn} from "../../global/GlobalEvents";
import {AuthQueries, USER_RULES} from "../../backend/authorization/AuthQueries";
import {ApolloClient, createHttpLink, DefaultOptions, InMemoryCache} from "@apollo/client";
import {setContext} from "@apollo/client/link/context";
import {Box, CircularProgress} from "@mui/material";
import {toast} from "react-toastify";

const idTokenKey = "id_token";
const accessTokenKey = "access_token";
const tokenTypeKey = "token_type";

function throwError(message: string): never {
	throw new Error(message);
}

export function LoginPage() {
	const url = window.location.href.replace("#", "?");
	
	const params = new URL(url).searchParams;
	
	if (params && params.has(idTokenKey) && params.has(accessTokenKey) && params.has(tokenTypeKey)) {
		const idToken = params.get(idTokenKey) ?? throwError(`Missing map value for idTokenKey: '${idTokenKey}'`);
		const accessToken = params.get(accessTokenKey) ?? throwError(`Missing map value for accessTokenKey: '${accessTokenKey}'`);
		const tokenType = params.get(tokenTypeKey) ?? throwError(`Missing map value for tokenTypeKey: '${tokenTypeKey}'`);
		
		const jwtDyn: any = jwtDecode<JwtPayload>(idToken);
		
		const jwt = new Jwt(
			jwtDyn.iat ?? throwError("Missing property value for 'iat'"),
			jwtDyn.exp ?? throwError("Missing property value for 'exp'")
		);
		
		applyUserSettings(idToken, accessToken, tokenType, jwtDyn, jwt);
		
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	} else {
		login();
		return <></>;
	}
}

async function applyUserSettings(
	idToken: string,
	accessToken: string,
	tokenType: string,
	jwtDyn: any,
	jwt: Jwt
) {
	const user = new KiziUser(
		jwtDyn.name,
		jwtDyn.email,
		jwtDyn.picture ?? null,
		jwtDyn["cognito:groups"] ?? [],
	);
	
	const authorizationClient = createApolloClient(
		`${Configuration.kiziBackendHost}/authorization/graphql`,
		tokenType,
		idToken,
		accessToken
	);
	
	const promise = authorizationClient.query<AuthQueries>({
		query: USER_RULES
	});
	const query = await toast.promise(promise, {
		pending: "Loading data from the server takes longer than expected, are you sure you are connected to the VPN?",
		error: "Failed to load data from the server, are you sure you are connected to the VPN?",
		success: "Successfully loaded account data."
	}, {
		delay: 3_000,
		autoClose: false,
		closeButton: true
	});
	
	const policy = query.data.userRules;
	
	const userSession = new UserSession(
		idToken,
		accessToken,
		Instant.ofEpochSecond(jwt.startedAt, 0),
		Instant.ofEpochSecond(jwt.expiresAt, 0),
		tokenType,
		jwt,
		user,
		policy
	);
	
	delayed(() => {
		GlobalState.userSession = userSession;
		GlobalEvents.userLoggedOn.emit(new UserLoggedOn(userSession));
	});
	
	const navigation = window.localStorage.getItem("login:navigation") ?? "/";
	window.localStorage.removeItem("login:navigation");
	window.location.assign(navigation);
}

export function login() {
	const url = new URL(window.location.href);
	const path = url.pathname + url.search;

	if (path !== "/" && path !== "/login")
		window.localStorage.setItem("login:navigation", path);
	else
		window.localStorage.removeItem("login:navigation");
	window.location.assign(Configuration.cognitoLoginUrl);
}

function createApolloClient(
	uri: string,
	tokenType: string,
	idToken: string,
	accessToken: string,
	options?: DefaultOptions,
): ApolloClient<any> {
	const httpLink = createHttpLink({
		uri: uri,
	});
	
	const authLink = setContext((_, { headers }) => {
		return {
			headers: {
				...headers,
				"X-Id-Token": `${tokenType} ${idToken}`,
				Authorization: `${tokenType} ${accessToken}`,
			},
		};
	});
	
	return new ApolloClient<any>({
		link: authLink.concat(httpLink),
		cache: new InMemoryCache(),
		defaultOptions: options,
	});
}
